import React, { useState, useEffect } from 'react';
import { Calendar, Users, Mail, Clock, Trash2, RefreshCw, LogOut, ChevronLeft, ChevronRight, AlertCircle, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = ({ onLogout }) => {
  const [stats, setStats] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [activeTab, setActiveTab] = useState('consultations');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [notification, setNotification] = useState(null);
  const [consultationPage, setConsultationPage] = useState(0);
  const [contactPage, setContactPage] = useState(0);
  const [consultationTotal, setConsultationTotal] = useState(0);
  const [contactTotal, setContactTotal] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      /* fetch error - logged server-side */
    }
  };

  const fetchConsultations = async () => {
    try {
      const statusParam = filter !== 'all' ? `&status=${filter}` : '';
      const res = await fetch(`${API_URL}/api/admin/consultations?limit=${ITEMS_PER_PAGE}&skip=${consultationPage * ITEMS_PER_PAGE}${statusParam}`);
      const data = await res.json();
      setConsultations(data.consultations);
      setConsultationTotal(data.total);
    } catch (err) {
      /* fetch error - logged server-side */
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/contacts?limit=${ITEMS_PER_PAGE}&skip=${contactPage * ITEMS_PER_PAGE}`);
      const data = await res.json();
      setContacts(data.contacts);
      setContactTotal(data.total);
    } catch (err) {
      /* fetch error - logged server-side */
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/scheduler-status`);
      const data = await res.json();
      setSchedulerStatus(data);
    } catch (err) {
      /* fetch error - logged server-side */
    }
  };

  const deleteConsultation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this consultation?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/consultations/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showNotification('Consultation deleted successfully');
        fetchConsultations();
        fetchStats();
      }
    } catch (err) {
      showNotification('Failed to delete consultation', 'error');
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/contacts/${id}`, { method: 'DELETE' });
      if (res.ok) {
        showNotification('Contact deleted successfully');
        fetchContacts();
        fetchStats();
      }
    } catch (err) {
      showNotification('Failed to delete contact', 'error');
    }
  };

  const triggerReminders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/trigger-reminders`, { method: 'POST' });
      if (res.ok) {
        showNotification('Reminders triggered successfully');
      }
    } catch (err) {
      showNotification('Failed to trigger reminders', 'error');
    }
  };

  const refreshData = () => {
    setLoading(true);
    Promise.all([fetchStats(), fetchConsultations(), fetchContacts(), fetchSchedulerStatus()])
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchConsultations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, consultationPage]);

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactPage]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (date) => {
    const today = new Date().toISOString().split('T')[0];
    if (date === today) return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Today</span>;
    if (date > today) return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Upcoming</span>;
    return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Past</span>;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
          {notification.message}
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-[#0d1442]">Admin Dashboard</h1>
              <span className="text-sm text-slate-500">BIZZ Europe</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={refreshData}
                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-[#1a237e] hover:bg-slate-100 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0d1442]">{stats.total_consultations}</p>
                  <p className="text-sm text-slate-500">Total Bookings</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0d1442]">{stats.upcoming_consultations}</p>
                  <p className="text-sm text-slate-500">Upcoming</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0d1442]">{stats.today_consultations}</p>
                  <p className="text-sm text-slate-500">Today</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0d1442]">{stats.past_consultations}</p>
                  <p className="text-sm text-slate-500">Past</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Mail className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0d1442]">{stats.total_contacts}</p>
                  <p className="text-sm text-slate-500">Contact Forms</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scheduler Status */}
        {schedulerStatus && (
          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500' : 'bg-red-500'}`} />
                <div>
                  <p className="font-medium text-[#0d1442]">Automated Reminder System</p>
                  <p className="text-sm text-slate-500">
                    {schedulerStatus.scheduler_running ? 'Running' : 'Stopped'} • 
                    {schedulerStatus.next_run && ` Next run: ${schedulerStatus.next_run.split('.')[0].replace('T', ' ')}`}
                  </p>
                </div>
              </div>
              <button
                onClick={triggerReminders}
                className="px-4 py-2 text-sm bg-[#1a237e] text-white rounded-lg hover:bg-[#0d1442] transition-colors"
              >
                Send Reminders Now
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => setActiveTab('consultations')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'consultations'
                ? 'bg-[#1a237e] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Consultations
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'contacts'
                ? 'bg-[#1a237e] text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
          >
            <Mail className="w-4 h-4" />
            Contact Forms
          </button>
        </div>

        {/* Consultations Tab */}
        {activeTab === 'consultations' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Filter */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="font-semibold text-[#0d1442]">Consultation Bookings</h2>
              <div className="flex gap-2">
                {['all', 'upcoming', 'today', 'past'].map((f) => (
                  <button
                    key={f}
                    onClick={() => { setFilter(f); setConsultationPage(0); }}
                    className={`px-3 py-1.5 text-sm rounded-lg capitalize transition-colors ${
                      filter === f
                        ? 'bg-[#1a237e] text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Reminder</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {consultations.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-[#0d1442]">{formatDate(c.date)}</div>
                        <div className="text-sm text-slate-500">{c.time_slot}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-[#0d1442]">{c.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">{c.company_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">{c.email}</div>
                        {c.phone && <div className="text-sm text-slate-500">{c.phone}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(c.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {c.reminder_sent ? (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Sent</span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">Pending</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => deleteConsultation(c.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Showing {consultationPage * ITEMS_PER_PAGE + 1} - {Math.min((consultationPage + 1) * ITEMS_PER_PAGE, consultationTotal)} of {consultationTotal}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setConsultationPage(Math.max(0, consultationPage - 1))}
                  disabled={consultationPage === 0}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setConsultationPage(consultationPage + 1)}
                  disabled={(consultationPage + 1) * ITEMS_PER_PAGE >= consultationTotal}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="font-semibold text-[#0d1442]">Contact Form Submissions</h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Message</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {contacts.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">{formatDateTime(c.created_at)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-[#0d1442]">{c.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">{c.company_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-600">{c.email}</div>
                        {c.phone && <div className="text-sm text-slate-500">{c.phone}</div>}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-600 max-w-xs truncate" title={c.message}>
                          {c.message}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => deleteContact(c.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Showing {contactPage * ITEMS_PER_PAGE + 1} - {Math.min((contactPage + 1) * ITEMS_PER_PAGE, contactTotal)} of {contactTotal}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setContactPage(Math.max(0, contactPage - 1))}
                  disabled={contactPage === 0}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setContactPage(contactPage + 1)}
                  disabled={(contactPage + 1) * ITEMS_PER_PAGE >= contactTotal}
                  className="p-2 rounded-lg border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-100"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
